import { useState } from "react"

import { X } from "lucide-react"

import { Field, FieldError } from "@/components/ui/Field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select"

interface ClassTimeStepProps {
  value: string
  onChange: (value: string) => void
  onValidation?: (isValid: boolean) => void
}

export const CLASS_TIME_OPTIONS = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "20 minutes", label: "20 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "45 minutes", label: "45 minutes" },
  { value: "1 hour", label: "1 hour" },
  { value: "1.5 hours", label: "1.5 hours" },
  { value: "2 hours", label: "2 hours" },
]

export function ClassTimeStep({
  value,
  onChange,
  onValidation,
}: ClassTimeStepProps) {
  const [touched, setTouched] = useState(false)
  const hasError = touched && !value

  function handleValueChange(newValue: string) {
    onChange(newValue)
    setTouched(true)
    onValidation?.(true)
  }

  return (
    <div className="tw-text-center tw-space-y-6 tw-pt-16">
      <h2 className="tw-text-3xl tw-pb-3 tw-font-bold tw-text-gray-800">
        How much time will students <br />
        have for this assignment?
      </h2>

      <Field>
        <Select value={value} onValueChange={handleValueChange}>
          <SelectTrigger
            className={`tw-w-full tw-max-w-lg tw-mx-auto tw-px-6 tw-py-4 tw-border-2 tw-border-dashed tw-rounded-sm tw-text-center tw-text-base tw-text-gray-500 placeholder:tw-text-gray-400 placeholder:tw-whitespace-pre-line tw-transition-all tw-resize-none focus:tw-outline-none focus:tw-ring-2 ${
              hasError
                ? "!tw-border-red-300 tw-bg-red-50 focus:tw-ring-red-500"
                : "!tw-border-emerald-600 focus:tw-border-emerald-800 focus:tw-ring-emerald-600"
            }`}
          >
            <SelectValue placeholder="Select time duration..." />
          </SelectTrigger>
          <SelectContent>
            {CLASS_TIME_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasError && (
          <FieldError>
            <span className="tw-flex tw-justify-center tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
              <X className="tw-w-4 tw-h-4" />
              Please select a time duration
            </span>
          </FieldError>
        )}
      </Field>
    </div>
  )
}
